import React from 'react';
import { Bolt, Trash } from '@saleshandy/icons';
import { McpSession } from '../../types';
import EmptyList from '../../../../../../shared/design-system/components/molecules/empty-list/empty-list';
import { Images } from '../../../../../../shared/app-constants';
import { accessibleOnClick } from '../../../../../../shared/utils/accessible-on-click';
import {
  ChatGPTLogo,
  ClaudeLogo,
  CursorLogo,
  VSCodeLogo,
} from '../mcp-tool-logos';

type Props = {
  sessions: McpSession[];
  onDisconnect: (session: McpSession) => void;
};

const formatDate = (dateStr: string): string => {
  const date = new Date(dateStr);
  const dd = String(date.getDate()).padStart(2, '0');
  const mm = String(date.getMonth() + 1).padStart(2, '0');
  const yyyy = date.getFullYear();
  return `${dd}-${mm}-${yyyy}`;
};

const formatRelativeTime = (dateStr: string): string => {
  const now = new Date();
  const date = new Date(dateStr);
  const diffMs = now.getTime() - date.getTime();
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMs / 3600000);
  const diffDays = Math.floor(diffMs / 86400000);

  if (diffMins < 1) return 'Just now';
  if (diffMins < 60) return `${diffMins}m ago`;
  if (diffHours < 24) return `${diffHours}h ago`;
  if (diffDays < 30) return `${diffDays}d ago`;
  return formatDate(dateStr);
};

const CLIENT_LOGO_MAP: Record<string, { icon: React.FC; bgClass: string }> = {
  chatgpt: { icon: ChatGPTLogo, bgClass: 'mcp-tool-icon--chatgpt' },
  claude: { icon: ClaudeLogo, bgClass: 'mcp-tool-icon--claude-web' },
  cursor: { icon: CursorLogo, bgClass: 'mcp-tool-icon--cursor' },
  'vs code': { icon: VSCodeLogo, bgClass: 'mcp-tool-icon--vscode' },
  vscode: { icon: VSCodeLogo, bgClass: 'mcp-tool-icon--vscode' },
};

const getClientLogo = (
  clientName: string | null,
): { icon: React.FC; bgClass: string } | null => {
  if (!clientName) return null;
  const name = clientName.toLowerCase();
  for (const [key, value] of Object.entries(CLIENT_LOGO_MAP)) {
    if (name.includes(key)) return value;
  }
  return null;
};

const McpSessionsTable: React.FC<Props> = ({ sessions, onDisconnect }) => {
  if (sessions.length === 0) {
    return (
      <EmptyList
        title="No active connections yet."
        description="Set up MCP to connect your first AI tool."
        imgSrc={Images.EmptyData1}
      />
    );
  }

  return (
    <div className="mcp-sessions-table-wrapper">
      <table className="mcp-sessions-table">
        <thead>
          <tr>
            <th>Connected Apps</th>
            <th>Connected By</th>
            <th>Connected On</th>
            <th>Last Activity</th>
            <th>Actions</th>
          </tr>
        </thead>
        <tbody>
          {sessions.map((session) => (
            <tr key={session.id} className="mcp-sessions-table__row">
              <td>
                <div className="mcp-sessions-table__tool-cell">
                  {(() => {
                    const logo = getClientLogo(session.clientName);
                    if (logo) {
                      const LogoIcon = logo.icon;
                      return (
                        <div
                          className={`mcp-sessions-table__tool-icon ${logo.bgClass}`}
                        >
                          <LogoIcon />
                        </div>
                      );
                    }
                    return (
                      <div className="mcp-sessions-table__tool-icon">
                        <Bolt width={14} height={14} />
                      </div>
                    );
                  })()}
                  <span className="mcp-sessions-table__tool-name">
                    {session.clientName || 'Unknown Client'}
                  </span>
                </div>
              </td>
              <td className="mcp-sessions-table__user">
                {`${session.user.firstName} ${session.user.lastName}`}
              </td>
              <td className="mcp-sessions-table__date">
                {formatDate(session.createdAt)}
              </td>
              <td className="mcp-sessions-table__date">
                {session.lastUsedAt
                  ? formatRelativeTime(session.lastUsedAt)
                  : 'Never'}
              </td>
              <td className="mcp-sessions-table__action-col">
                <span
                  className="mcp-sessions-table__delete-btn"
                  {...accessibleOnClick(() => onDisconnect(session))}
                  title="Disconnect"
                >
                  <Trash width={15} height={15} />
                </span>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

export default McpSessionsTable;
